import { getInvestigationBaseSchema } from '../investigationBaseSchema';
import { AnalysisMode } from '@customer/store/apis/investigationsApi';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  AnalysisModeLimits,
  MIN_INVESTIGATION_ASSETS,
  MIN_INVESTIGATION_DURATION,
} from '../config';

interface UseSchemaProps {
  mode: AnalysisMode;
}

export const useCustomInvestigationSchema = ({ mode }: UseSchemaProps) => {
  const { t } = useTranslation();

  const schema = useMemo(() => {
    const maxDuration = AnalysisModeLimits[mode].maxDuration;
    const maxAssets = AnalysisModeLimits[mode].maxAssets;

    const baseSchema = getInvestigationBaseSchema(t);

    return baseSchema.extend({
      analysisMode: z.nativeEnum(AnalysisMode, {
        message: t('Validation.Specify', { field: 'AnalysisMode' }),
      }),
      startDate: z
        .string()
        .nullable()
        .refine(v => v !== null, { message: t('Validation.Specify', { field: 'StartDate' }) }),
      startTime: z
        .string()
        .nullable()
        .refine(v => v !== null, { message: t('Validation.Specify', { field: 'StartTime' }) }),
      duration: z
        .number()
        .min(MIN_INVESTIGATION_DURATION, {
          message: t('Validation.Minimum', {
            count: MIN_INVESTIGATION_DURATION,
            unit: 'minute',
            context: 'short',
          }),
        })
        .max(maxDuration, {
          message: t('Validation.Maximum', {
            count: maxDuration,
            unit: 'minute',
            context: 'short',
          }),
        })
        .int({ message: t('Validation.ValueMustBeInteger') }),
      assetIds: z
        .array(z.number())
        .min(MIN_INVESTIGATION_ASSETS, {
          message: t('Validation.SelectMinimum', {
            count: MIN_INVESTIGATION_ASSETS,
            field: 'Asset',
          }),
        })
        .max(maxAssets, {
          message: t('Validation.LimitExceeded', { count: maxAssets, field: 'Asset' }),
        }),
    });
  }, [t, mode]);

  return schema;
};
